import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, FocusEvent as ReactFocusEvent, ReactNode } from "react";
import { FIGURE_KEY_ATTR } from "@/zapi-frontend/shared/lib/figureRefs";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
import { FIGURE_CARD_ID, FigureCard } from "./_FigureCard";

const FigureRefsContext = createContext<Map<string, TLedgerFigure> | null>(null);

export const useFigureRefs = () => useContext(FigureRefsContext);

// Long enough to cross the gap to the card, which is deliberately hoverable.
const OPEN_MS = 90;
const CLOSE_MS = 300;

type TOpenCard = { key: string; anchor: HTMLElement };

// One hover surface for every figure trigger under it: a chapter's prose is markdown turned into
// HTML, so its triggers are plain spans rather than components, and the stat bands and tables
// render theirs in React. Reading the key off the nearest `data-figure-key` serves both.
export function FigureRefsProvider({ figures, children }: { figures: TLedgerFigure[] | undefined; children: ReactNode }) {
  const byKey = useMemo(() => new Map((figures ?? []).map((figure) => [figure.key, figure])), [figures]);
  const [open, setOpen] = useState<TOpenCard | null>(null);
  const openTimer = useRef<number>();
  const closeTimer = useRef<number>();

  const clearTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };

  const scheduleOpen = useCallback((anchor: HTMLElement) => {
    const key = anchor.getAttribute(FIGURE_KEY_ATTR);
    if (!key) return;
    clearTimers();
    openTimer.current = window.setTimeout(() => setOpen({ key, anchor }), OPEN_MS);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => setOpen(null), CLOSE_MS);
  }, []);

  const closeNow = useCallback(() => {
    clearTimers();
    setOpen(null);
  }, []);

  useEffect(() => clearTimers, []);

  // While open, the trigger points at the card (restoring whatever it described before) and Enter
  // or Space moves focus into it — the card holds a link and formula pills, so a keyboard reader
  // needs a way in; Escape closes and hands focus back, a click anywhere else just closes, which
  // is also how a touch reader gets rid of a card that a tap opened.
  useEffect(() => {
    if (!open) return;
    const { anchor } = open;
    const described = anchor.getAttribute("aria-describedby");
    anchor.setAttribute("aria-describedby", FIGURE_CARD_ID);
    anchor.setAttribute("aria-expanded", "true");
    const card = () => document.getElementById(FIGURE_CARD_ID);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeNow();
        anchor.focus();
      } else if ((event.key === "Enter" || event.key === " ") && event.target === anchor) {
        event.preventDefault();
        const first = card()?.querySelector<HTMLElement>("a, button");
        (first ?? card())?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target && (anchor.contains(target) || target.closest(`#${FIGURE_CARD_ID}`))) return;
      closeNow();
    };
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      const inside = next && (anchor.contains(next) || next.closest(`#${FIGURE_CARD_ID}`));
      if (!inside && card()?.contains(event.target as Node)) closeNow();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      if (described === null) anchor.removeAttribute("aria-describedby");
      else anchor.setAttribute("aria-describedby", described);
      anchor.removeAttribute("aria-expanded");
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [open, closeNow]);

  const triggerOf = (target: EventTarget | null) =>
    target instanceof Element ? target.closest<HTMLElement>(`[${FIGURE_KEY_ATTR}]`) : null;

  const onPointerOver = (event: ReactPointerEvent<HTMLDivElement>) => {
    const trigger = triggerOf(event.target);
    if (trigger) scheduleOpen(trigger);
  };
  const onPointerOut = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (triggerOf(event.target)) scheduleClose();
  };
  const onFocus = (event: ReactFocusEvent<HTMLDivElement>) => {
    const trigger = triggerOf(event.target);
    if (trigger) scheduleOpen(trigger);
  };
  const onBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    if (!triggerOf(event.target)) return;
    const next = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (next?.closest(`#${FIGURE_CARD_ID}`)) return;
    closeNow();
  };

  const figure = open ? byKey.get(open.key) : undefined;

  return (
    <FigureRefsContext.Provider value={byKey}>
      <div className="contents" onPointerOver={onPointerOver} onPointerOut={onPointerOut} onFocus={onFocus} onBlur={onBlur}>
        {children}
      </div>
      {open && figure && (
        <FigureCard
          figure={figure}
          anchor={open.anchor}
          byKey={byKey}
          onAway={closeNow}
          onPointerEnter={clearTimers}
          onPointerLeave={scheduleClose}
        />
      )}
    </FigureRefsContext.Provider>
  );
}
